import { useState, useEffect, useCallback } from"react";
import { useNavigate, useSearchParams } from"react-router-dom";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { ChevronDown, ChevronRight, CheckCircle2, CheckCircle, Circle, Clock, ListChecks, List, GanttChart, Plus, Building2, Layers, Package, Trash2 } from"lucide-react";
import { toast } from"sonner";
import TimelineView from"@/components/timeline/TimelineView";
import { PlannedCostsPanel } from '@/components/project-progress/PlannedCostsPanel';
import { JobPlannedPrice } from '@/components/project-progress/JobPlannedPrice';
import { usePlannedCosts } from '@/components/project-progress/usePlannedCosts';
import { jobQuantity, measureLabel, numberLabel } from '@/components/project-progress/plannedCostUtils';

const ProjectProgressPage = ({ user, onLogout }) => {
 const { t, i18n } = useTranslation();
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [currentProject, setCurrentProject] = useState(null);
 const [jobs, setJobs] = useState([]);
 const [expandedPhases, setExpandedPhases] = useState([]);
 const [loading, setLoading] = useState(true);
 const [viewMode, setViewMode] = useState("list");
 const [dialogOpen, setDialogOpen] = useState(false);
 const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
 const [selectedJob, setSelectedJob] = useState(null);
 const [taskPartner, setTaskPartner] = useState(null);
 const [billingPreset, setBillingPreset] = useState({ preset_type: "free_select", batch_minimum: "", threshold: "" });
 const [savingPreset, setSavingPreset] = useState(false);
 const [addTaskOpen, setAddTaskOpen] = useState(false);
 const [newTask, setNewTask] = useState({ job_name: "", phase: 3, measurement_unit: "m2", tracking_level: "per_unit", apply_to_all_projects: false });
 const [addingTask, setAddingTask] = useState(false);
 const [materialSummaries, setMaterialSummaries] = useState({});
 const [editSubtasks, setEditSubtasks] = useState([]);
 const [realCosts, setRealCosts] = useState(null);
 const [savingJob, setSavingJob] = useState(false);
 const [formData, setFormData] = useState({
 status:"not_started",
 start_date:"",
 completion_date:"",
 notes:""
 });
 const [budgetData, setBudgetData] = useState({
 total_m2:"",
 cost_per_m2:"",
 land_cost:"",
 communal_tax:"",
 planned_total_budget:"",
 currency:"EUR"
 });
 const canPlan = ['owner', 'management'].includes(user.role);
 const planning = usePlannedCosts(selectedProject, canPlan, jobs, currentProject?.currency);
 const plannedJobs = Object.fromEntries((planning.data?.jobs || []).map(job => [job.job_id, job]));

 // Helper function to translate job names
 const translateJobName = (jobName) => {
 const translatedName = t(`projectProgress.jobNames.${jobName}`, { defaultValue: null });
 return translatedName || jobName;
 };

 // Helper function to translate status
 const translateStatus = (status) => {
 return t(`projectProgress.statusLabels.${status}`, { defaultValue: status });
 };

 const phaseNames = {
 1: t('projectProgress.phases.phase1'),
 2: t('projectProgress.phases.phase2'),
 3: t('projectProgress.phases.phase3'),
 4: t('projectProgress.phases.phase4')
 };

 const phaseWeights = {
 1: 10, // Documentation
 2: 40, // Structural & Masonry Works
 3: 40, // Installations & Finishing Works
 4: 10 // Finalization & Legalization
 };

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 if (selectedProject) {
 loadCurrentProject();
 loadJobs();
 loadMaterialSummaries();
 loadRealCosts();
 }
 }, [selectedProject]);

 const loadCurrentProject = async () => {
 try {
 const response = await axios.get(`/projects/${selectedProject}`);
 setCurrentProject(response.data);
 setBudgetData({
 total_m2: response.data.total_m2 ||"",
 cost_per_m2: response.data.cost_per_m2 ||"",
 land_cost: response.data.land_cost ||"",
 communal_tax: response.data.communal_tax ||"",
 planned_total_budget: response.data.planned_total_budget ||"",
 currency: response.data.currency ||"EUR"
 });
 } catch (error) {
 console.error("Failed to load project details");
 }
 };

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 if (response.data.length > 0) {
 const urlProject = searchParams.get("project");
 const match = urlProject && response.data.find(p => p.id === urlProject);
 setSelectedProject(match ? match.id : response.data[0].id);
 }
 } catch (error) {
 toast.error(t('messages.loadFailed'));
 }
 };


 const loadMaterialSummaries = async () => {
 try {
 const response = await axios.get(`/job-materials/summaries/${selectedProject}`);
 setMaterialSummaries(response.data);
 } catch (e) {
 // Silently fail - summaries are optional display
 }
 };

 const loadRealCosts = async () => {
 if (user?.role !== "owner") return;
 try {
  const response = await axios.get(`/offers/${selectedProject}/real-costs`);
  setRealCosts(response.data);
 } catch (e) { setRealCosts(null); }
 };


 const loadJobs = useCallback(async () => {
 setLoading(true);
 try {
 const response = await axios.get(`/project-progress/${selectedProject}`);
 if (response.data.length === 0) {
 await axios.post(`/project-progress/initialize/${selectedProject}`);
 const newResponse = await axios.get(`/project-progress/${selectedProject}`);
 setJobs(newResponse.data);
 toast.success(t('projectProgress.initialized'));
 } else {
 setJobs(response.data);
 }
 } catch (error) {
 console.error("Error loading jobs", error);
 toast.error(t('messages.loadFailed'));
 } finally {
 setLoading(false);
 }
 }, [selectedProject]);

 const handleBudgetSubmit = async (e) => {
 e.preventDefault();
 try {
 // Convert empty strings to null for numeric fields
 const payload = {
 total_m2: budgetData.total_m2 ? parseFloat(budgetData.total_m2) : null,
 cost_per_m2: budgetData.cost_per_m2 ? parseFloat(budgetData.cost_per_m2) : null,
 land_cost: budgetData.land_cost ? parseFloat(budgetData.land_cost) : null,
 communal_tax: budgetData.communal_tax ? parseFloat(budgetData.communal_tax) : null,
 planned_total_budget: budgetData.planned_total_budget ? parseFloat(budgetData.planned_total_budget) : null,
 currency: budgetData.currency
 };
 
 await axios.patch(`/projects/${selectedProject}`, payload);
 toast.success(t('messages.saveSuccess'));
 setBudgetDialogOpen(false);
 loadCurrentProject();
 } catch (error) {
 console.error("Budget update error:", error);
 toast.error(error.response?.data?.detail || t('messages.saveFailed'));
 }
 };

 const calculatePlannedBudget = () => {
 const m2 = parseFloat(budgetData.total_m2);
 const costPerM2 = parseFloat(budgetData.cost_per_m2);
 if (m2 && costPerM2) {
 return (m2 * costPerM2).toFixed(2);
 }
 return null;
 };

 const togglePhase = (phase) => {
 setExpandedPhases(prev =>
 prev.includes(phase) ? prev.filter(p => p !== phase) : [...prev, phase]
 );
 };

 const deleteJob = async (job) => {
  if (!window.confirm(`Delete "${job.job_name}"? This will remove all subtasks, materials, and progress data for this job.`)) return;
  try {
   await axios.delete(`/project-progress/jobs/${job.id}`);
   toast.success(`"${job.job_name}" deleted`);
   loadJobs();
   loadMaterialSummaries();
  } catch (err) {
   toast.error(err.response?.data?.detail || "Failed to delete job");
  }
 };

 const openJobDialog = async (job) => {
 setSelectedJob(job);
 setFormData({
 status: job.status,
 area_m2: job.area_m2 ?? "",
 planned_unit_price: job.planned_unit_price ?? "",
 measurement_unit: job.measurement_unit ||"m2",
 tracking_level: job.tracking_level || "per_unit",
 primary_subtask_id: job.primary_subtask_id || "",
 start_date: job.start_date ? new Date(job.start_date).toISOString().split('T')[0] :"",
 completion_date: job.completion_date ? new Date(job.completion_date).toISOString().split('T')[0] :"",
 notes: job.notes ||""
 });
 setBillingPreset({
 preset_type: job.billing_preset || "free_select",
 batch_minimum: job.billing_batch_minimum || "",
 threshold: job.billing_threshold || ""
 });
 setDialogOpen(true);

 // Fetch subtasks for primary subtask selector
 try {
  const stRes = await axios.get(`/project-progress/jobs/${job.id}/subtasks`);
  setEditSubtasks(stRes.data.subtasks || []);
 } catch { setEditSubtasks([]); }
 
 // Fetch partner if assigned
 if (job.partner_id) {
 try {
 const response = await axios.get(`/project-progress/${job.id}/partner`);
 setTaskPartner(response.data.partner);
 } catch (error) {
 console.error("Failed to load partner", error);
 setTaskPartner(null);
 }
 } else {
 setTaskPartner(null);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (savingJob) return;
 setSavingJob(true);
 try {
 const { planned_unit_price, ...jobFields } = formData;
 await axios.patch(`/project-progress/${selectedJob.id}`, {
 ...jobFields,
 ...(canPlan ? { planned_unit_price: planned_unit_price === '' || planned_unit_price == null ? null : Number(planned_unit_price) } : {}),
 area_m2: formData.area_m2 === '' || formData.area_m2 == null ? null : Number(formData.area_m2),
 measurement_unit: formData.measurement_unit,
 tracking_level: formData.tracking_level,
 start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
 completion_date: formData.completion_date ? new Date(formData.completion_date).toISOString() : null
 });
 toast.success(t('messages.saveSuccess'));
 setDialogOpen(false);
 loadJobs();
 loadMaterialSummaries();
 } catch (error) {
 // Check for dependency error
 if (error.response?.status === 400 && error.response?.data?.detail?.error ==="dependency_not_met") {
 const deps = error.response.data.detail.missing_dependencies;
 toast.error(
 <div>
 <div className="font-bold">{t('projectProgress.cannotStartTask')}</div>
 <div className="text-sm mt-1">{t('projectProgress.prerequisitesNotCompleted')}</div>
 <ul className="text-xs mt-1 list-disc list-inside">
 {deps.map(dep => <li key={dep}>{dep}</li>)}
 </ul>
 </div>,
 { duration: 8000 }
 );
 } else {
 toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('messages.saveFailed'));
 }
 }
 finally { setSavingJob(false); }
 };


 const handleAddTask = async () => {
   if (!newTask.job_name.trim()) { toast.error("Enter a task name"); return; }
   setAddingTask(true);
   try {
     const res = await axios.post("/job-materials/add-task", {
       project_id: selectedProject,
       phase: newTask.phase,
       job_name: newTask.job_name.trim(),
       measurement_unit: newTask.measurement_unit,
       tracking_level: newTask.tracking_level,
       apply_to_all_projects: newTask.apply_to_all_projects,
     });
     toast.success(res.data.message);
     setAddTaskOpen(false);
     setNewTask({ job_name: "", phase: 3, measurement_unit: "m2", tracking_level: "per_unit", apply_to_all_projects: false });
     loadJobs();
   } catch (e) { toast.error(e.response?.data?.detail || "Failed to add task"); }
   finally { setAddingTask(false); }
 };


 const handleCompletePhase = async (phase, totalTasks) => {
 const phaseNames = {
 1: t('projectProgress.phases.phase1'),
 2: t('projectProgress.phases.phase2'),
 3: t('projectProgress.phases.phase3'),
 4: t('projectProgress.phases.phase4')
 };

 if (!window.confirm(
 `Mark Phase ${phase} – ${phaseNames[phase]} as completed?\n\n` +
 `This will mark all ${totalTasks} tasks as completed with today's date.\n\n` +
 `Are you sure?`
 )) {
 return;
 }

 try {
 const response = await axios.post(`/project-progress/${selectedProject}/complete-phase/${phase}`);
 toast.success(
 `✅ Phase ${phase} completed! ${response.data.tasks_updated} task(s) marked as completed.`,
 { duration: 5000 }
 );
 loadJobs();
 } catch (error) {
 toast.error(error.response?.data?.detail || t('messages.saveFailed'));
 }
 };

 const formatDate = (dateString) => {
 if (!dateString) return"";
 return new Date(dateString).toLocaleDateString('en-GB', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 });
 };

 const getStatusIcon = (status) => {
 switch (status) {
 case"completed": return <CheckCircle className="w-5 h-5 text-green-600"/>;
 case"in_progress": return <Clock className="w-5 h-5 text-blue-600"/>;
 default: return <Circle className="w-5 h-5 text-slate-300"/>;
 }
 };

 const getStatusBadge = (status) => {
 const styles = {
 not_started:"bg-white/60 text-[#334155]",
 in_progress:"bg-blue-500/10 text-blue-700",
 completed:"bg-emerald-500/10 text-emerald-700"
 };
 return <Badge className={styles[status]}>{translateStatus(status)}</Badge>;
 };

 // Group jobs by phase
 const jobsByPhase = {
 1: jobs.filter(j => j.phase === 1),
 2: jobs.filter(j => j.phase === 2),
 3: jobs.filter(j => j.phase === 3),
 4: jobs.filter(j => j.phase === 4)
 };

 // Calculate statistics
 const getPhaseStats = (phase) => {
 const phaseJobs = jobsByPhase[phase];
 const total = phaseJobs.length;
 const completed = phaseJobs.filter(j => j.status === 'completed').length;
 const inProgress = phaseJobs.filter(j => j.status === 'in_progress').length;
 // Use average of individual task progress percentages
 const totalProgress = phaseJobs.reduce((sum, j) => sum + (j.progress_percentage || (j.status === 'completed' ? 100 : 0)), 0);
 const percentage = total > 0 ? totalProgress / total : 0;
 return { total, completed, inProgress, percentage };
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 min-w-0" data-testid="project-progress-page">
 <div className="flex flex-wrap items-center justify-between gap-4">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{t('projectProgress.title')}</h1>
 <p className="text-slate-500 mt-1">{t('projectProgress.subtitle')}</p>
 </div>
 <div className="flex flex-wrap items-center gap-3 min-w-0">
 {/* Add Task button */}
 {(user.role ==="owner"|| user.role ==="management"|| user.role ==="engineer") && selectedProject && (
 <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setAddTaskOpen(true)} data-testid="add-task-btn">
 <Plus className="w-3.5 h-3.5 mr-1"/> {t('projectProgress.addTask')}
 </Button>
 )}
 {/* View Toggle */}
 <div className="flex bg-white/60 rounded-sm p-0.5"data-testid="view-mode-toggle">
 <button
 className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
 viewMode ==="list"
 ?"bg-white shadow-sm text-slate-900"
 :"text-slate-500 hover:text-slate-700:text-zinc-200"
 }`}
 onClick={() => setViewMode("list")}
 data-testid="view-mode-list-btn"
 >
 <List className="w-3.5 h-3.5"/>
 {t('projectProgress.list')}
 </button>
 <button
 className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
 viewMode ==="timeline"
 ?"bg-white shadow-sm text-slate-900"
 :"text-slate-500 hover:text-slate-700:text-zinc-200"
 }`}
 onClick={() => setViewMode("timeline")}
 data-testid="view-mode-timeline-btn"
 >
 <GanttChart className="w-3.5 h-3.5"/>
 {t('projectProgress.timeline')}
 </button>
 </div>

 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-full sm:w-64" data-testid="progress-project-select">
 <SelectValue placeholder={t('common.selectProject')} />
 </SelectTrigger>
 <SelectContent data-testid="progress-project-menu">
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id} data-testid={`progress-project-${project.id}`}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>

 {/* Project Budget & Info Card */}
 {currentProject && (
 <Card className="">
 <CardHeader>
 <div className="flex flex-wrap items-center justify-between gap-2">
 <CardTitle className="text-lg">{t('projectProgress.projectBudgetArea')}</CardTitle>
 <Button size="sm"variant="outline"onClick={() => setBudgetDialogOpen(true)} data-testid="progress-budget-edit">
 {t('common.edit')}
 </Button>
 </div>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 [&>div]:min-w-0 [&>div]:break-words">
 <div className="bg-white p-4 rounded-sm border border-blue-100">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.totalArea')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {currentProject.total_m2 ? `${currentProject.total_m2.toLocaleString()} m²` : t('common.noData')}
 </div>
 </div>
 <div className="bg-white p-4 rounded-sm border border-indigo-100">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.netTotalArea')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {currentProject.net_total_m2 ? `${currentProject.net_total_m2.toLocaleString()} m²` : t('common.noData')}
 </div>
 <div className="text-[11px] text-slate-500 mt-1">{t('projectProgress.netAreaHint')}</div>
 </div>
 <div className="bg-white p-4 rounded-sm border border-blue-100">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.costPerM2')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {currentProject.cost_per_m2 ? `${currentProject.cost_per_m2.toLocaleString()} ${currentProject.currency}` : t('common.noData')}
 </div>
 </div>
 <div className="bg-white p-4 rounded-sm border border-orange-100">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.landCost')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {currentProject.land_cost ? `${currentProject.land_cost.toLocaleString()} ${currentProject.currency}` : t('common.noData')}
 </div>
 </div>
 <div className="bg-white p-4 rounded-sm border border-slate-200">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.communalTax')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {currentProject.communal_tax && currentProject.net_total_m2
 ? `${(currentProject.communal_tax * currentProject.net_total_m2).toLocaleString()} ${currentProject.currency}`
 : t('common.noData')}
 </div>
 {currentProject.communal_tax && (
 <div className="text-[11px] text-slate-500 mt-1">{currentProject.communal_tax} {currentProject.currency}/m²</div>
 )}
 </div>
 <div className="bg-white p-4 rounded-sm border border-slate-200">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.constructionBudget')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {currentProject.planned_total_budget 
 ? `${currentProject.planned_total_budget.toLocaleString()} ${currentProject.currency}`
 : (currentProject.total_m2 && currentProject.cost_per_m2)
 ? `${(currentProject.total_m2 * currentProject.cost_per_m2).toLocaleString()} ${currentProject.currency}`
 : t('common.noData')}
 </div>
 {currentProject.total_m2 && currentProject.cost_per_m2 && !currentProject.planned_total_budget && (
 <div className="text-[11px] text-slate-500 mt-1">{t('projectProgress.planned')}</div>
 )}
 </div>
 <div className="bg-white p-4 rounded-sm border border-slate-200">
 <div className="text-sm text-slate-600 mb-1">{t('projectProgress.totalProjectBudget')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {(() => {
 const construction = currentProject.planned_total_budget || 
 (currentProject.total_m2 && currentProject.cost_per_m2 ? currentProject.total_m2 * currentProject.cost_per_m2 : 0);
 const land = currentProject.land_cost || 0;
 const communal = (currentProject.communal_tax && currentProject.net_total_m2) ? currentProject.communal_tax * currentProject.net_total_m2 : 0;
 const total = construction + land + communal;
 return total > 0 ? `${total.toLocaleString()} ${currentProject.currency}` : t('common.noData');
 })()}
 </div>
 <div className="text-[11px] text-slate-500 mt-1">{t('projectProgress.constructionBudget')} + {t('projectProgress.landCost')} + {t('projectProgress.communalTax')}</div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Real Costs from Accepted Offers — Owner Only */}
 {canPlan && selectedProject && <PlannedCostsPanel key={selectedProject} planning={planning} disabled={user.demoMode} />}
 {user?.role === "owner" && realCosts && (
 <Card className="mt-4" data-testid="real-costs-card">
  <CardHeader className="pb-2">
  <CardTitle className="text-lg">{t('projectProgress.realCosts')}</CardTitle>
  </CardHeader>
  <CardContent>
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 [&>div]:min-w-0 [&>div]:break-words">
   {[1, 2, 3, 4].map(phase => (
   <div key={phase} className="bg-white p-4 rounded-sm border border-emerald-100" data-testid={`real-cost-phase-${phase}`}>
    <div className="text-sm text-slate-600 mb-1">{t('projectProgress.phases.phase' + phase, `Phase ${phase}`)}</div>
    <div className="text-2xl font-bold text-slate-900">
    {realCosts.phases[phase]
     ? `${realCosts.phases[phase].toLocaleString()} ${currentProject?.currency || "EUR"}`
     : "—"}
    </div>
   </div>
   ))}
   <div className="bg-emerald-50 p-4 rounded-sm border border-emerald-300" data-testid="real-cost-total">
   <div className="text-sm font-semibold text-emerald-700 mb-1">{t('projectProgress.totalRealCost')}</div>
   <div className="text-2xl font-bold text-emerald-900">
    {realCosts.total > 0
    ? `${realCosts.total.toLocaleString()} ${currentProject?.currency || "EUR"}`
    : "—"}
   </div>
   </div>
  </div>
  </CardContent>
 </Card>
 )}

 {loading ? (
 <div className="text-center py-12">{t('common.loading')}</div>
 ) : viewMode ==="timeline"? (
 <TimelineView
 jobs={jobs}
 phaseNames={phaseNames}
 phaseWeights={phaseWeights}
 selectedProject={selectedProject}
 onJobsReload={loadJobs}
 translateJobName={translateJobName}
 />
 ) : (
 <div className="space-y-4">
 {/* Engineers don't see Phase 1 (Documentation) - they start from Phase 2 (Construction) */}
 {(user.role ==="engineer"? [2, 3, 4] : [1, 2, 3, 4]).map(phase => {
 const stats = getPhaseStats(phase);
 const isExpanded = expandedPhases.includes(phase);
 
 return (
 <Card key={phase} className="border-2">
 <CardHeader
 data-testid={`progress-phase-${phase}`}
 className="cursor-pointer hover:bg-white/80 transition-colors"
 onClick={() => togglePhase(phase)}
 >
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-3 flex-1 min-w-0">
 {isExpanded ? (
 <ChevronDown className="w-5 h-5 text-blue-600"/>
 ) : (
 <ChevronRight className="w-5 h-5 text-blue-600"/>
 )}
 <div>
 <div className="flex flex-wrap items-center gap-3">
 <CardTitle className="text-base md:text-lg break-words">
 {phaseNames[phase]}
 </CardTitle>
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700 border-blue-200/60">
 {phaseWeights[phase]}% {t('projectProgress.ofProject')}
 </Badge>
 </div>
 <div className="text-sm text-slate-600 mt-1">
 {stats.completed} / {stats.total} {t('projectProgress.complete').toLowerCase()} • {stats.inProgress} {t('tasks.inProgress').toLowerCase()}
 </div>
 </div>
 </div>
 
 {(user.role ==="owner"|| user.role ==="management"|| user.role ==="engineer") && stats.completed < stats.total && (
 <Button
 size="sm"
 variant="outline"
 data-testid={`complete-progress-phase-${phase}`}
 className="border-green-300 text-green-700 hover:bg-green-50/80 whitespace-normal h-auto min-h-8 text-left"
 onClick={(e) => {
 e.stopPropagation();
 handleCompletePhase(phase, stats.total);
 }}
 >
 <CheckCircle2 className="w-4 h-4 mr-2"/>
 {t('projectProgress.markPhaseCompleted')}
 </Button>
 )}
 
 {stats.completed === stats.total && (
 <Badge className="bg-emerald-500/10 text-emerald-700 border-green-300">
 <CheckCircle2 className="w-4 h-4 mr-1"/>
 {t('projectProgress.complete')}
 </Badge>
 )}
 <div className="flex items-center gap-4">
 <div className="text-right">
 <div className="text-2xl font-bold text-slate-900">{stats.percentage.toFixed(0)}%</div>
 <div className="text-[11px] text-slate-500">{t('projectProgress.complete')}</div>
 </div>
 <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
 <div
 className="h-full bg-blue-600 transition-all"
 style={{ width: `${stats.percentage}%` }}
 />
 </div>
 </div>
 </div>
 </CardHeader>

 {isExpanded && (
 <CardContent className="pt-0">
 <div className="space-y-2 mt-4">
 {jobsByPhase[phase].map((job) => (
 <div
 key={job.id}
 data-testid={`progress-job-${job.id}`}
 className="border rounded-sm p-3 hover:bg-white/80 cursor-pointer transition-colors"
 onClick={() => openJobDialog(job)}
 >
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div className="flex items-start gap-3 flex-1 min-w-0">
 {getStatusIcon(job.status)}
 <div className="flex-1 min-w-0">
 <div className="font-medium text-slate-900 break-words">{translateJobName(job.job_name)}</div>
 <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-slate-600">
 {materialSummaries[job.id] ? (
 <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`material-total-${job.id}`}>
   <Package className="w-3 h-3"/>
   {numberLabel(jobQuantity(job, materialSummaries[job.id]).quantity, i18n.language)} {measureLabel(jobQuantity(job, materialSummaries[job.id]).unit)}
 </span>
 ) : job.area_m2 != null ? (
 <span className="font-semibold text-blue-600" data-testid={`manual-job-quantity-${job.id}`}>{job.area_m2.toLocaleString()} {measureLabel(job.measurement_unit)}</span>
 ) : null}
 {job.start_date && (
 <span>{t('projects.startDate')}: {formatDate(job.start_date)}</span>
 )}
 {job.completion_date && (
 <span>{t('tasks.completed')}: {formatDate(job.completion_date)}</span>
 )}
 </div>
 {canPlan && plannedJobs[job.id] && <div className="mt-2 text-xs text-indigo-700 break-words" data-testid={`planned-job-cost-${job.id}`}>
 {t('plannedCosts.jobTotal')}: {numberLabel(plannedJobs[job.id].planned_cost, i18n.language)} {planning.data.currency}
 {plannedJobs[job.id].unit_price != null && <> · {numberLabel(plannedJobs[job.id].unit_price, i18n.language)} {planning.data.currency}/{measureLabel(plannedJobs[job.id].unit || job.measurement_unit)}</>}
 </div>}
 {job.dependencies && job.dependencies.length > 0 && (
 <div className="text-xs text-orange-600 mt-1 flex items-center gap-1">
 <span>⚠️ {t('validation.required')}:</span>
 <span className="font-medium">{job.dependencies.length} prerequisite{job.dependencies.length > 1 ? 's' : ''}</span>
 </div>
 )}
 {job.notes && (
 <div className="text-[11px] text-slate-500 mt-1">{job.notes}</div>
 )}
 </div>
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <Button
 variant="outline"
 size="sm"
 className="h-7 px-2 text-xs"
 data-testid={`job-materials-btn-${job.id}`}
 onClick={(e) => {
 e.stopPropagation();
 navigate(`/project-progress/${selectedProject}/job/${job.id}/materials`);
 }}
 >
 <Package className="w-3.5 h-3.5 mr-1"/>
 {t('plannedCosts.materialsButton')}
 </Button>
 <Button
 variant="outline"
 size="sm"
 className="h-7 px-2 text-xs"
 data-testid={`job-subtasks-btn-${job.id}`}
 onClick={(e) => {
 e.stopPropagation();
 navigate(`/project-progress/${selectedProject}/job/${job.id}`);
 }}
 >
 <ListChecks className="w-3.5 h-3.5 mr-1"/>
 {t('jobSubtasks.subtasks')}
 </Button>
 {getStatusBadge(job.status)}
 <Button
  variant="ghost"
  size="sm"
  className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
  data-testid={`delete-job-${job.id}`}
  onClick={(e) => { e.stopPropagation(); deleteJob(job); }}
 >
  <Trash2 className="w-3.5 h-3.5"/>
 </Button>
 </div>
 </div>
 {/* Progress bar - below the flex row */}
 {(job.progress_percentage > 0 || job.status === 'completed') && (
 <div className="mt-2 flex items-center gap-2">
 <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
 <div
 className={`h-full rounded-full transition-all duration-500 ${
 job.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'
 }`}
 style={{ width: `${job.status === 'completed' ? 100 : Math.min(job.progress_percentage || 0, 100)}%` }}
 />
 </div>
 <span className="text-[11px] font-semibold text-slate-500 tabular-nums w-10 text-right">
 {job.status === 'completed' ? 100 : Math.round(job.progress_percentage || 0)}%
 </span>
 </div>
 )}
 </div>
 ))}
 </div>
 </CardContent>
 )}
 </Card>
 );
 })}
 </div>
 )}

 {/* Job Update Dialog */}
 <Dialog open={dialogOpen} onOpenChange={open => { if (!savingJob) setDialogOpen(open); }}>
 <DialogContent className="w-[calc(100%_-_2rem)]" data-testid="progress-job-dialog" closeLabel={t('common.cancel')} aria-describedby={undefined}>
 <DialogHeader>
 <DialogTitle data-testid="progress-job-dialog-title">{t('projectProgress.updateJob')}</DialogTitle>
 </DialogHeader>
 {selectedJob && (
 <form onSubmit={handleSubmit} className="space-y-4">
 <div className="bg-white/50 p-3 rounded">
 <div className="text-sm text-slate-500">{t('projectProgress.job')}</div>
 <div className="font-semibold">{translateJobName(selectedJob.job_name)}</div>
 <div className="text-[11px] text-slate-500 mt-1">
 {phaseNames[selectedJob.phase]}
 </div>
 {selectedJob.dependencies && selectedJob.dependencies.length > 0 && (
 <div className="mt-3 p-2 bg-orange-50/80 border border-orange-200/60 rounded">
 <div className="text-xs font-semibold text-orange-800 mb-1">⚠️ {t('projectProgress.prerequisitesRequired')}:</div>
 <ul className="text-xs text-orange-700 list-disc list-inside space-y-0.5">
 {selectedJob.dependencies.map((dep, idx) => {
 const depJob = jobs.find(j => j.job_name === dep);
 const isCompleted = depJob?.status === 'completed';
 return (
 <li key={idx} className={isCompleted ?"text-green-700 line-through":""}>
 {translateJobName(dep)} {isCompleted ?"✓":""}
 </li>
 );
 })}
 </ul>
 </div>
 )}
 
 {taskPartner && (
 <div className="mt-3 p-3 bg-blue-50/80 border border-blue-200/60 rounded">
 <div className="flex items-center gap-2 mb-2">
 <span className="text-sm font-semibold text-blue-900">👷 {t('projectProgress.assignedPartner')}</span>
 </div>
 <div className="space-y-1 text-xs">
 <div className="flex items-center justify-between">
 <span className="text-blue-700 font-semibold">{t('partners.companyName')}:</span>
 <span className="text-blue-900">{taskPartner.company_name}</span>
 </div>
 <div className="flex items-center justify-between">
 <span className="text-blue-700 font-semibold">{t('partners.contactPerson')}:</span>
 <span className="text-blue-900">{taskPartner.contact_person}</span>
 </div>
 <div className="flex items-center justify-between">
 <span className="text-blue-700 font-semibold">{t('common.phone')}:</span>
 <span className="text-blue-900">{taskPartner.phone}</span>
 </div>
 <div className="flex items-center justify-between">
 <span className="text-blue-700 font-semibold">{t('common.email')}:</span>
 <span className="text-blue-900 text-xs">{taskPartner.email}</span>
 </div>
 {taskPartner.contract_value && (
 <div className="flex items-center justify-between pt-1 border-t border-blue-200/60">
 <span className="text-blue-700 font-semibold">{t('reservations.contractValue')}:</span>
 <span className="text-blue-900 font-bold">€{taskPartner.contract_value.toLocaleString()}</span>
 </div>
 )}
 </div>
 </div>
 )}
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="status">{t('common.status')} *</Label>
 <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
 <SelectTrigger data-testid="progress-job-status">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem data-testid="progress-job-status-not-started" value="not_started">{translateStatus('not_started')}</SelectItem>
 <SelectItem data-testid="progress-job-status-in-progress" value="in_progress">{translateStatus('in_progress')}</SelectItem>
 <SelectItem data-testid="progress-job-status-completed" value="completed">{translateStatus('completed')}</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="area_m2">{t('plannedCosts.quantity')} ({measureLabel(formData.measurement_unit)})</Label>
 <div className="flex gap-2">
 <Input
 id="area_m2"
 data-testid="progress-job-quantity-input"
 type="number"
 min="0"
 step="0.01"
 placeholder="e.g., 200"
 value={formData.area_m2}
 onChange={(e) => setFormData({ ...formData, area_m2: e.target.value })}
 className="flex-1 min-w-0"
 />
 <Select value={formData.measurement_unit} onValueChange={(val) => setFormData({ ...formData, measurement_unit: val })}>
 <SelectTrigger className="w-24 shrink-0" data-testid="progress-job-unit-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {['m2', 'm3', 'pcs', 'linear_m', 'kg', 'ton'].map(unit => <SelectItem key={unit} value={unit} data-testid={`progress-job-unit-${unit}`}>{measureLabel(unit)}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 </div>
 </div>

 <div>
 <Label>{t('plannedCosts.trackingLevel')}</Label>
 <Select value={formData.tracking_level || "per_unit"} onValueChange={(value) => setFormData({ ...formData, tracking_level: value })}>
 <SelectTrigger data-testid="tracking-level-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="per_unit">Per Apartment — track materials per unit</SelectItem>
 <SelectItem value="per_floor">Per Floor — track materials per floor</SelectItem>
 </SelectContent>
 </Select>
 </div>

 {editSubtasks.length > 0 && (
 <div>
  <Label>{t('plannedCosts.primarySubtask')}</Label>
  <Select value={formData.primary_subtask_id || "auto"} onValueChange={(value) => setFormData({ ...formData, primary_subtask_id: value === "auto" ? "" : value })}>
  <SelectTrigger data-testid="primary-subtask-select">
   <SelectValue />
  </SelectTrigger>
  <SelectContent>
   <SelectItem value="auto" data-testid="primary-subtask-auto">{t('plannedCosts.autoPrimary')}</SelectItem>
   {editSubtasks.map(st => (
   <SelectItem key={st.id} value={st.id} data-testid={`primary-subtask-${st.id}`}>{st.name}</SelectItem>
   ))}
  </SelectContent>
  </Select>
  <p className="text-[11px] text-slate-500 mt-1">{t('plannedCosts.primaryHint')}</p>
 </div>
 )}

 {canPlan && <JobPlannedPrice job={selectedJob} summary={materialSummaries[selectedJob.id]} form={formData} setForm={setFormData} currency={currentProject?.currency || 'EUR'} disabled={savingJob || user.demoMode} />}

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="start_date">{t('projects.startDate')}</Label>
 <Input
 id="start_date"
 data-testid="progress-job-start-date"
 type="date"
 value={formData.start_date}
 onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
 />
 </div>
 <div>
 <Label htmlFor="completion_date">{t('projectProgress.completionDate')}</Label>
 <Input
 id="completion_date"
 data-testid="progress-job-completion-date"
 type="date"
 value={formData.completion_date}
 onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
 />
 </div>
 </div>

 <div>
 <Label htmlFor="notes">{t('common.notes')}</Label>
 <Textarea
 id="notes"
 data-testid="progress-job-notes"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 rows={3}
 />
 </div>

 {/* Billing Preset */}
 <div className="border-t pt-4">
 <div className="flex items-center justify-between mb-3">
 <div>
 <Label className="text-sm font-semibold text-slate-700">Billing Preset</Label>
 <p className="text-xs text-slate-500 mt-0.5">Controls when contractor can submit a report for engineer approval</p>
 </div>
 </div>
 <div className="space-y-3">
 <Select value={billingPreset.preset_type} onValueChange={(v) => setBillingPreset({ ...billingPreset, preset_type: v })}>
 <SelectTrigger className="rounded-sm">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="by_floor">By Floor — submit when a full floor is done</SelectItem>
 <SelectItem value="by_apartment">By Apartment — submit per apartment batch</SelectItem>
 <SelectItem value="by_subtask">By Subtask — submit when a work stage is done building-wide</SelectItem>
 <SelectItem value="by_unit_m2">By m² — submit every N m² of completed work</SelectItem>
 <SelectItem value="free_select">Free Select — contractor submits anytime</SelectItem>
 </SelectContent>
 </Select>
 {billingPreset.preset_type === "by_apartment" && (
 <div>
 <Label className="text-xs text-slate-600">Minimum apartments before submit button appears</Label>
 <Input type="number" min="1" placeholder="e.g., 5" className="mt-1 rounded-sm"
 value={billingPreset.batch_minimum}
 onChange={(e) => setBillingPreset({ ...billingPreset, batch_minimum: e.target.value })} />
 </div>
 )}
 {billingPreset.preset_type === "by_unit_m2" && (
 <div>
 <Label className="text-xs text-slate-600">Report unlocks every N m² completed</Label>
 <Input type="number" min="1" placeholder="e.g., 50" className="mt-1 rounded-sm"
 value={billingPreset.threshold}
 onChange={(e) => setBillingPreset({ ...billingPreset, threshold: e.target.value })} />
 </div>
 )}
 <Button type="button" variant="outline" size="sm" disabled={savingPreset} className="rounded-sm w-full"
 onClick={async () => {
 setSavingPreset(true);
 try {
 await axios.patch(`/project-progress/${selectedJob.id}/billing-preset`, {
 preset_type: billingPreset.preset_type,
 batch_minimum: billingPreset.batch_minimum ? parseInt(billingPreset.batch_minimum) : null,
 threshold: billingPreset.threshold ? parseFloat(billingPreset.threshold) : null
 });
 toast.success("Billing preset saved");
 loadJobs();
 } catch { toast.error("Failed to save preset"); }
 finally { setSavingPreset(false); }
 }}>
 {savingPreset ? "Saving…" : "Save Billing Preset"}
 </Button>
 </div>
 </div>

 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1" disabled={savingJob || user.demoMode} data-testid="progress-job-save">{savingJob ? t('common.loading') : t('common.update')}</Button>
 <Button type="button"variant="outline"onClick={() => setDialogOpen(false)} className="flex-1" disabled={savingJob} data-testid="progress-job-cancel">
 {t('common.cancel')}
 </Button>
 </div>
 </form>
 )}
 </DialogContent>
 </Dialog>

 {/* Budget Edit Dialog */}
 <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
 <DialogContent className="w-[calc(100%_-_2rem)] max-w-2xl">
 <DialogHeader>
 <DialogTitle>{t('projectProgress.editBudgetArea')}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleBudgetSubmit} className="space-y-4">
 <div>
 <Label htmlFor="total_m2">{t('projectProgress.totalArea')} *</Label>
 <Input
 id="total_m2"
 type="number"
 step="0.01"
 value={budgetData.total_m2}
 onChange={(e) => setBudgetData({ ...budgetData, total_m2: e.target.value })}
 placeholder="e.g., 5000"
 />
 <p className="text-[11px] text-slate-500 mt-1">{t('projectProgress.totalM2Hint')}</p>
 </div>

 <div>
 <Label htmlFor="land_cost">{t('projectProgress.landCost')}</Label>
 <Input
 id="land_cost"
 type="number"
 step="0.01"
 value={budgetData.land_cost}
 onChange={(e) => setBudgetData({ ...budgetData, land_cost: e.target.value })}
 placeholder="e.g., 500000"
 />
 <p className="text-[11px] text-slate-500 mt-1">{t('projectProgress.landCostHint')}</p>
 </div>

 <div>
 <Label htmlFor="communal_tax">{t('projectProgress.communalTax')}</Label>
 <Input
 id="communal_tax"
 type="number"
 step="0.01"
 value={budgetData.communal_tax}
 onChange={(e) => setBudgetData({ ...budgetData, communal_tax: e.target.value })}
 placeholder="e.g., 50"
 />
 {budgetData.communal_tax && currentProject?.net_total_m2 && (
 <p className="text-xs text-purple-600 mt-1 font-medium">
 Total: {(parseFloat(budgetData.communal_tax) * currentProject.net_total_m2).toLocaleString()} {budgetData.currency} ({currentProject.net_total_m2.toLocaleString()} m² net × {budgetData.communal_tax} {budgetData.currency}/m²)
 </p>
 )}
 </div>

 <div className="border-t pt-4">
 <p className="text-sm font-semibold text-[#334155] mb-3">{t('projectProgress.budgetMethod')}:</p>
 
 <div className="bg-blue-50/80 border border-blue-200/60 rounded-sm p-4 mb-4">
 <p className="text-sm font-semibold text-blue-900 mb-3">{t('projectProgress.option1CostPerM2')}</p>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="cost_per_m2">{t('projectProgress.costPerM2')} *</Label>
 <Input
 id="cost_per_m2"
 type="number"
 step="0.01"
 value={budgetData.cost_per_m2}
 onChange={(e) => setBudgetData({ ...budgetData, cost_per_m2: e.target.value, planned_total_budget:""})}
 placeholder="e.g., 800"
 />
 <p className="text-xs text-slate-600 mt-1">{t('projectProgress.costPerM2Hint')}</p>
 </div>
 <div>
 <Label htmlFor="currency">{t('projectProgress.currency')}</Label>
 <Select
 value={budgetData.currency}
 onValueChange={(value) => setBudgetData({ ...budgetData, currency: value })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="EUR">EUR (€)</SelectItem>
 <SelectItem value="USD">USD ($)</SelectItem>
 <SelectItem value="MKD">MKD (ден)</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 {budgetData.total_m2 && budgetData.cost_per_m2 && (
 <div className="mt-3 p-2 bg-white rounded border border-blue-300">
 <p className="text-sm text-blue-900">
 <span className="font-semibold">{t('projectProgress.calculatedBudget')}:</span> {calculatePlannedBudget()} {budgetData.currency}
 </p>
 <p className="text-xs text-slate-600 mt-1">
 ({budgetData.total_m2} m² × {budgetData.cost_per_m2} {budgetData.currency}/m²)
 </p>
 </div>
 )}
 </div>

 <div className="text-center text-sm text-slate-500 my-3">{t('common.or')}</div>

 <div className="bg-green-50/80 border border-green-200/60 rounded-sm p-4">
 <p className="text-sm font-semibold text-green-900 mb-3">{t('projectProgress.option2DirectBudget')}</p>
 <div>
 <Label htmlFor="planned_total_budget">{t('projectProgress.totalPlannedBudget')} *</Label>
 <Input
 id="planned_total_budget"
 type="number"
 step="0.01"
 value={budgetData.planned_total_budget}
 onChange={(e) => setBudgetData({ ...budgetData, planned_total_budget: e.target.value, cost_per_m2:""})}
 placeholder="e.g., 4000000"
 />
 <p className="text-xs text-slate-600 mt-1">{t('projectProgress.directBudgetHint')}</p>
 </div>
 </div>
 </div>

 <div className="bg-white/50 border border-slate-200 rounded-sm p-3 text-sm">
 <p className="font-semibold text-[#334155] mb-1">Summary:</p>
 <div className="text-slate-600 space-y-1">
 <div>• Total Area: {budgetData.total_m2 || '—'} m²</div>
 <div>• Land Cost: {budgetData.land_cost ? `${parseFloat(budgetData.land_cost).toLocaleString()} ${budgetData.currency}` : '—'}</div>
 <div>• Communal Tax: {budgetData.communal_tax && currentProject?.net_total_m2 ? `${(parseFloat(budgetData.communal_tax) * currentProject.net_total_m2).toLocaleString()} ${budgetData.currency}` : '—'}</div>
 <div>• Construction Budget: {
 budgetData.planned_total_budget 
 ? `${parseFloat(budgetData.planned_total_budget).toLocaleString()} ${budgetData.currency}` 
 : (budgetData.total_m2 && budgetData.cost_per_m2)
 ? `${calculatePlannedBudget()} ${budgetData.currency} (calculated)`
 : '—'
 }</div>
 <div className="font-semibold text-green-700 pt-1 border-t border-slate-200">
 Total Project Budget: {(() => {
 const construction = budgetData.planned_total_budget 
 ? parseFloat(budgetData.planned_total_budget)
 : (budgetData.total_m2 && budgetData.cost_per_m2)
 ? parseFloat(calculatePlannedBudget())
 : 0;
 const land = budgetData.land_cost ? parseFloat(budgetData.land_cost) : 0;
 const communal = (budgetData.communal_tax && currentProject?.net_total_m2) ? parseFloat(budgetData.communal_tax) * currentProject.net_total_m2 : 0;
 const total = construction + land + communal;
 return total > 0 ? `${total.toLocaleString()} ${budgetData.currency}` : '—';
 })()}
 </div>
 </div>
 </div>

 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1">Save Budget</Button>
 <Button type="button"variant="outline"onClick={() => setBudgetDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>

 {/* Add New Task Dialog */}
 <Dialog open={addTaskOpen} onOpenChange={setAddTaskOpen}>
 <DialogContent className="w-[calc(100%_-_2rem)] max-w-md">
 <DialogHeader>
 <DialogTitle>Add New Task</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>Task Name *</Label>
 <Input data-testid="new-task-name" placeholder="e.g., Insulation Works" value={newTask.job_name} onChange={(e) => setNewTask({ ...newTask, job_name: e.target.value })} />
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Phase</Label>
 <Select value={String(newTask.phase)} onValueChange={(v) => setNewTask({ ...newTask, phase: parseInt(v) })}>
 <SelectTrigger data-testid="new-task-phase"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="1">Phase 1 - Documentation & Preparation</SelectItem>
 <SelectItem value="2">Phase 2 - Structural & Masonry</SelectItem>
 <SelectItem value="3">Phase 3 - Installations & Finishing</SelectItem>
 <SelectItem value="4">Phase 4 - Finalization & Legalization</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Unit of Measure</Label>
 <Select value={newTask.measurement_unit} onValueChange={(v) => setNewTask({ ...newTask, measurement_unit: v })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="m2">m²</SelectItem>
 <SelectItem value="pcs">Pieces</SelectItem>
 <SelectItem value="linear_m">Linear m</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 <div>
 <Label>Tracking Level</Label>
 <Select value={newTask.tracking_level} onValueChange={(v) => setNewTask({ ...newTask, tracking_level: v })}>
 <SelectTrigger data-testid="new-task-tracking"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="per_unit">Per Apartment</SelectItem>
 <SelectItem value="per_floor">Per Floor</SelectItem>
 </SelectContent>
 </Select>
 <p className="text-[11px] text-slate-500 mt-1">Per Apartment = track per unit. Per Floor = one entry per floor level.</p>
 </div>
 <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-sm">
 <input type="checkbox" id="apply-all-projects" checked={newTask.apply_to_all_projects}
   onChange={(e) => setNewTask({ ...newTask, apply_to_all_projects: e.target.checked })}
   className="rounded" data-testid="apply-all-projects-checkbox" />
 <label htmlFor="apply-all-projects" className="text-sm text-indigo-800 cursor-pointer">
 <span className="font-semibold">{t('projectProgress.applyToAllProjects')}</span>
 <span className="block text-[11px] text-indigo-600 mt-0.5">{t('projectProgress.applyToAllProjectsHint')}</span>
 </label>
 </div>
 <Button onClick={handleAddTask} disabled={addingTask || !newTask.job_name.trim()} className="w-full" data-testid="add-task-submit-btn">
 {addingTask ? t('projectProgress.addingTask') : t('projectProgress.addTask')}
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default ProjectProgressPage;
